import React from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useSearchParams } from 'react-router-dom';

// Hooks
import { useCantonData } from './hooks/useCantonData';

// Components
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ErrorBoundary } from './components/ErrorBoundary';
import { OverviewTab } from './components/tabs/OverviewTab';
import { ValidatorsTab } from './components/tabs/ValidatorsTab';
import { SuperValidatorsTab } from './components/tabs/SuperValidatorsTab';
import { TransactionsTab } from './components/tabs/TransactionsTab';
import { AppsTab } from './components/tabs/AppsTab';
import { ChatTab } from './components/tabs/ChatTab';
import { DownloadTab } from './components/tabs/DownloadTab';
import { GlobalStyles } from './styles/GlobalStyles';

// Route to tab mapping
const routeToTab = {
  '/': 'overview',
  '/chat': 'chat',
  '/apps': 'apps',
  '/validators': 'validators',
  '/super-validators': 'super validators',
  '/transactions': 'transactions',
};

const tabToRoute = {
  'overview': '/',
  'chat': '/chat',
  'apps': '/apps',
  'validators': '/validators',
  'super validators': '/super-validators',
  'transactions': '/transactions',
};

function AppContent() {
  const { data, loading, error, lastUpdate } = useCantonData();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const activeTab = routeToTab[location.pathname] || 'overview';
  const highlightParty = searchParams.get('highlight');

  const setActiveTab = (tab) => {
    navigate(tabToRoute[tab] || '/');
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(180deg, #0a0c10 0%, #0f1218 50%, #0a0c10 100%)',
      color: '#e5e7eb',
      fontFamily: '"Inter", -apple-system, sans-serif',
      position: 'relative',
    }}>
      {/* Background grid effect */}
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundImage: `
          linear-gradient(rgba(0, 255, 136, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0, 255, 136, 0.03) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px',
        pointerEvents: 'none',
      }} />

      <Header
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        loading={loading}
        lastUpdate={lastUpdate}
      />

      {/* Main Content */}
      <main style={{ padding: '16px', maxWidth: '1600px', width: '100%', margin: '0 auto', flex: 1 }}>
        {error && (
          <div style={{
            background: 'rgba(255, 71, 87, 0.1)',
            border: '1px solid rgba(255, 71, 87, 0.3)',
            borderRadius: '4px',
            padding: '12px 16px',
            marginBottom: '24px',
            color: '#ff4757',
            fontSize: '13px',
          }}>{error}</div>
        )}

        <Routes>
          <Route path="/" element={<OverviewTab data={data} loading={loading} setActiveTab={setActiveTab} />} />
          <Route path="/chat" element={<ChatTab />} />
          <Route path="/apps" element={<AppsTab />} />
          <Route path="/validators" element={<ValidatorsTab data={data} loading={loading} highlightParty={highlightParty} />} />
          <Route path="/super-validators" element={<SuperValidatorsTab data={data} loading={loading} highlightParty={highlightParty} />} />
          <Route path="/transactions" element={<TransactionsTab data={data} loading={loading} />} />
          <Route path="/connect-node" element={<DownloadTab />} />
        </Routes>
      </main>

      <Footer />

      <GlobalStyles />
    </div>
  );
}

// Main App with Router and Error Boundary
export default function CantonExplorer() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
